import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, Navigate } from "react-router-dom";
import { Activity, Focus, History as HistoryIcon, Settings as SettingsIcon, Save, ChevronDown, ChevronRight } from "lucide-react";
import {
  listarTrabalhandoAgora,
  definirTarefaAtual,
  listarHistoricoSessoes,
  getHorarioComercial,
  setHorarioComercial,
  getOperacaoConfig,
  setOperacaoConfig,
  HistoricoItem,
  HorarioComercial,
  OperacaoConfig,
} from "../services/me";
import { useAuth } from "../hooks/useAuth";
import { VINCULO_LABEL, VINCULO_BADGE } from "../utils/vinculo";
import { Avatar } from "../components/Avatar";
import { ProjetoAvatar } from "../components/ProjetoAvatar";
import { listarUsuariosAtivos } from "../services/usuarios";

function tempoDesde(iso: string | null): string {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return "agora";
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}min`;
}

function fmtDur(ms: number): string {
  if (ms < 60_000) return "0min";
  const min = Math.floor(ms / 60_000);
  if (min < 60) return `${min}min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m === 0 ? `${h}h` : `${h}h ${String(m).padStart(2, "0")}min`;
}

const PRESETS: Array<{ id: string; label: string; range: () => [Date, Date] }> = [
  { id: "hoje",   label: "Hoje",       range: () => { const a = new Date(); a.setHours(0,0,0,0); const b = new Date(); b.setHours(23,59,59,999); return [a, b]; } },
  { id: "ontem",  label: "Ontem",      range: () => { const a = new Date(); a.setDate(a.getDate()-1); a.setHours(0,0,0,0); const b = new Date(a); b.setHours(23,59,59,999); return [a, b]; } },
  { id: "semana", label: "Esta semana", range: () => {
    const a = new Date(); const dow = a.getDay(); a.setDate(a.getDate() - dow); a.setHours(0,0,0,0);
    const b = new Date(); b.setHours(23,59,59,999);
    return [a, b];
  } },
  { id: "mes",    label: "Este mês",    range: () => {
    const a = new Date(); a.setDate(1); a.setHours(0,0,0,0);
    const b = new Date(); b.setHours(23,59,59,999);
    return [a, b];
  } },
  { id: "30d",    label: "Últimos 30 dias", range: () => {
    const b = new Date(); b.setHours(23,59,59,999);
    const a = new Date(); a.setDate(a.getDate()-30); a.setHours(0,0,0,0);
    return [a, b];
  } },
];

export function TrabalhandoAgora() {
  const { can } = useAuth();
  if (!can(["admin", "gestor"])) return <Navigate to="/" replace />;
  const [aba, setAba] = useState<"agora" | "historico">("agora");

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100 inline-flex items-center gap-2">
          <Activity size={20} className="text-emerald-600" /> Trabalhando
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
          Monitoramento em tempo real + histórico de atividades por usuário.
        </p>
      </div>

      {/* Abas */}
      <div className="inline-flex rounded-lg bg-slate-100 dark:bg-slate-800 p-1">
        <button
          onClick={() => setAba("agora")}
          className={`px-4 py-1.5 rounded-md text-sm font-medium inline-flex items-center gap-1.5 transition-colors ${
            aba === "agora" ? "bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 shadow-sm" : "text-slate-500 dark:text-slate-400 hover:text-slate-700"
          }`}
        >
          <Activity size={14} /> Agora
        </button>
        <button
          onClick={() => setAba("historico")}
          className={`px-4 py-1.5 rounded-md text-sm font-medium inline-flex items-center gap-1.5 transition-colors ${
            aba === "historico" ? "bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 shadow-sm" : "text-slate-500 dark:text-slate-400 hover:text-slate-700"
          }`}
        >
          <HistoryIcon size={14} /> Histórico
        </button>
      </div>

      {aba === "agora" && <AbaAgora />}
      {aba === "historico" && <AbaHistorico />}
    </div>
  );
}

function AbaAgora() {
  const qc = useQueryClient();
  const { data: lista = [], isLoading, refetch } = useQuery({
    queryKey: ["trabalhando-agora"],
    queryFn: listarTrabalhandoAgora,
    // Atualização mais viva (Adriel: "trocou de card e não apareceu"). O SSE já
    // invalida essa query; intervalo curto + refetch ao focar a aba são a rede de segurança.
    refetchInterval: 20000,
    refetchOnWindowFocus: true,
  });

  // (Mantém só pra referência; placeholder caso queiramos forçar pausa depois.)
  void useMutation; void definirTarefaAtual; void qc;

  return (
    <>
      <div className="flex justify-between items-center gap-2">
        <p className="text-xs text-slate-500">Atualiza em tempo real via SSE. Concluir/arquivar libera automaticamente.</p>
        <button onClick={() => refetch()} className="text-xs bg-white hover:bg-slate-50 border border-slate-200 px-3 py-1.5 rounded-lg text-slate-600">
          Atualizar
        </button>
      </div>

      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800 text-slate-500 uppercase text-xs">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Pessoa</th>
              <th className="text-left px-4 py-2 font-medium">Tarefa</th>
              <th className="text-left px-4 py-2 font-medium">Projeto</th>
              <th className="text-left px-4 py-2 font-medium">Tempo (útil)</th>
            </tr>
          </thead>
          <tbody className="text-slate-700 dark:text-slate-200">
            {isLoading && (<tr><td colSpan={4} className="px-4 py-8 text-center text-slate-400">Carregando…</td></tr>)}
            {!isLoading && lista.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-400"><Focus size={16} className="inline mr-1" />Ninguém focando agora.</td></tr>
            )}
            {lista.map((u) => (
              <tr key={u.id} className="border-t border-slate-100 dark:border-slate-800">
                <td className="px-4 py-2">
                  <div className="flex items-center gap-2">
                    <Avatar nome={u.nome} fotoUrl={u.fotoUrl} tamanho={28} bgFallback="bg-blue-100" textFallback="text-blue-700" />
                    <div className="min-w-0">
                      <p className="font-medium truncate">{u.nome}</p>
                      <div className="flex items-center gap-1">
                        <span className="text-[11px] text-slate-400 truncate">{u.perfil}</span>
                        {u.vinculo && VINCULO_LABEL[u.vinculo] && (
                          <span className={`text-[9px] px-1 py-0.5 rounded font-medium ${VINCULO_BADGE[u.vinculo]}`}>{VINCULO_LABEL[u.vinculo]}</span>
                        )}
                        {(u as any).ehAtendente && (
                          <span className="text-[9px] px-1 py-0.5 rounded font-medium bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300">Atendente</span>
                        )}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-2">
                  {u.tarefaAtual ? (
                    <Link to={`/tarefas?tarefa=${u.tarefaAtual.id}`} className="text-blue-600 hover:underline">{u.tarefaAtual.titulo}</Link>
                  ) : <span className="text-slate-400">—</span>}
                </td>
                <td className="px-4 py-2">
                  {u.tarefaAtual?.projeto ? (
                    <Link to={`/projetos/${u.tarefaAtual.projeto.id}`} className="inline-flex items-center gap-1.5 hover:underline">
                      <ProjetoAvatar nome={u.tarefaAtual.projeto.nome} fotoUrl={u.tarefaAtual.projeto.fotoUrl} tamanho="xs" />
                      <span className="truncate">{u.tarefaAtual.projeto.nome}</span>
                    </Link>
                  ) : <span className="text-slate-400">avulsa</span>}
                </td>
                <td className="px-4 py-2 tabular-nums">
                  <div className="font-medium text-slate-700 dark:text-slate-200">{fmtDur(u.tempoUtilFocoMs)}</div>
                  <div className="text-[11px] text-slate-400">iniciou há {tempoDesde(u.tarefaAtualDesde)}</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function AbaHistorico() {
  const { can } = useAuth();
  const podeConfig = can(["gestor", "admin"]);
  const [presetId, setPresetId] = useState<string>("semana");
  const [usuarioId, setUsuarioId] = useState("");
  const [mostrarConfig, setMostrarConfig] = useState(false);

  const [inicio, fim] = useMemo(() => {
    const p = PRESETS.find((x) => x.id === presetId) || PRESETS[0];
    return p.range();
  }, [presetId]);

  const { data: usuarios = [] } = useQuery({
    queryKey: ["usuarios-ativos"],
    queryFn: listarUsuariosAtivos,
  });

  const { data: items = [], isLoading, refetch } = useQuery({
    queryKey: ["historico-sessoes", presetId, usuarioId],
    queryFn: () => listarHistoricoSessoes({
      inicio: inicio.toISOString(),
      fim: fim.toISOString(),
      usuarioId: usuarioId || undefined,
    }),
    // Refresh leve a cada 30s pra refletir tempo acumulado das sessões abertas.
    refetchInterval: 30000,
  });

  const totais = useMemo(() => {
    return items.reduce(
      (acc, it) => ({ bruto: acc.bruto + it.totalBrutoMs, util: acc.util + it.totalUtilMs }),
      { bruto: 0, util: 0 },
    );
  }, [items]);

  return (
    <>
      <div className="flex items-center gap-2 flex-wrap">
        <div className="inline-flex rounded-lg bg-slate-100 dark:bg-slate-800 p-1">
          {PRESETS.map((p) => (
            <button
              key={p.id}
              onClick={() => setPresetId(p.id)}
              className={`px-3 py-1 rounded-md text-xs font-medium ${
                presetId === p.id ? "bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 shadow-sm" : "text-slate-500 dark:text-slate-400 hover:text-slate-700"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <select
          value={usuarioId}
          onChange={(e) => setUsuarioId(e.target.value)}
          className="px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-sm outline-none focus:border-blue-500"
        >
          <option value="">Todos os usuários</option>
          {(usuarios || []).filter((u: any) => u.perfil !== "cliente").map((u: any) => (
            <option key={u.id} value={u.id}>{u.nome}</option>
          ))}
        </select>
        <button
          onClick={() => refetch()}
          className="text-xs bg-white hover:bg-slate-50 border border-slate-200 px-3 py-1.5 rounded-lg text-slate-600"
        >
          Atualizar
        </button>
        {podeConfig && (
          <button
            onClick={() => setMostrarConfig((v) => !v)}
            className={`inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border ${mostrarConfig ? "bg-blue-50 border-blue-200 text-blue-700" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}
          >
            <SettingsIcon size={12} /> Configurações
          </button>
        )}
      </div>

      {mostrarConfig && podeConfig && (
        <div className="space-y-4">
          <OperacaoConfigEditor />
          <HorarioComercialEditor />
        </div>
      )}

      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800 text-slate-500 uppercase text-xs">
            <tr>
              <th className="w-6 px-2" />
              <th className="text-left px-4 py-2 font-medium">Pessoa</th>
              <th className="text-left px-4 py-2 font-medium">Tarefa</th>
              <th className="text-left px-4 py-2 font-medium">Projeto</th>
              <th className="text-right px-4 py-2 font-medium">Sessões</th>
              <th className="text-right px-4 py-2 font-medium">Total bruto</th>
              <th className="text-right px-4 py-2 font-medium">Total útil</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (<tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">Carregando…</td></tr>)}
            {!isLoading && items.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">Sem registros no período.</td></tr>
            )}
            {items.map((it, i) => (
              <LinhaHistorico key={`${it.usuario.id}-${it.tarefa.id}-${i}`} it={it} />
            ))}
          </tbody>
          {items.length > 0 && (
            <tfoot className="bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-200 font-semibold">
              <tr>
                <td className="px-2" />
                <td className="px-4 py-2" colSpan={4}>Total geral</td>
                <td className="px-4 py-2 text-right tabular-nums">{fmtDur(totais.bruto)}</td>
                <td className="px-4 py-2 text-right tabular-nums">{fmtDur(totais.util)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      <p className="text-[11px] text-slate-500">
        <strong>Bruto</strong>: tempo cronométrico real das sessões. <strong>Útil</strong>: descontando sábados/domingos e horários fora da janela comercial.
      </p>
    </>
  );
}

function LinhaHistorico({ it }: { it: HistoricoItem }) {
  const [aberto, setAberto] = useState(false);
  return (
    <>
      <tr className="border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50">
        <td className="px-2">
          <button onClick={() => setAberto((v) => !v)} className="text-slate-400 hover:text-slate-700">
            {aberto ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
        </td>
        <td className="px-4 py-2">
          <div className="flex items-center gap-2">
            <Avatar nome={it.usuario.nome} fotoUrl={it.usuario.fotoUrl} tamanho={24} bgFallback="bg-blue-100" textFallback="text-blue-700" />
            <span className="truncate">{it.usuario.nome}</span>
          </div>
        </td>
        <td className="px-4 py-2">
          <Link to={`/tarefas?tarefa=${it.tarefa.id}`} className="text-blue-600 hover:underline">{it.tarefa.titulo}</Link>
        </td>
        <td className="px-4 py-2">
          {it.tarefa.projeto ? (
            <Link to={`/projetos/${it.tarefa.projeto.id}`} className="hover:underline truncate inline-block max-w-[180px]">{it.tarefa.projeto.nome}</Link>
          ) : <span className="text-slate-400">avulsa</span>}
        </td>
        <td className="px-4 py-2 text-right tabular-nums text-slate-500">{it.sessoes.length}</td>
        <td className="px-4 py-2 text-right tabular-nums">{fmtDur(it.totalBrutoMs)}</td>
        <td className="px-4 py-2 text-right tabular-nums font-medium">{fmtDur(it.totalUtilMs)}</td>
      </tr>
      {aberto && (
        <tr className="bg-slate-50/50 dark:bg-slate-800/30 border-t border-slate-100 dark:border-slate-800">
          <td />
          <td colSpan={6} className="px-4 py-2">
            <ul className="space-y-1 text-xs text-slate-600 dark:text-slate-300">
              {it.sessoes.map((s) => (
                <li key={s.id} className="flex items-center gap-2 tabular-nums">
                  <span>{new Date(s.inicio).toLocaleString("pt-BR")}</span>
                  <span className="text-slate-400">→</span>
                  <span>{s.fim ? new Date(s.fim).toLocaleString("pt-BR") : <em className="text-emerald-600">aberta</em>}</span>
                  {s.fechadaPor && <span className="text-[10px] text-slate-400 ml-2">({s.fechadaPor})</span>}
                </li>
              ))}
            </ul>
          </td>
        </tr>
      )}
    </>
  );
}

// Decisões do admin/gestor: visibilidade do funcionário + pausa automática.
const VIS_OPCOES: Array<{ id: OperacaoConfig["visibilidadeFuncionario"]; titulo: string; desc: string }> = [
  { id: "alocadas", titulo: "Apenas alocadas", desc: "Funcionário vê só as tarefas atribuídas a ele (e onde é co-responsável)." },
  { id: "envolvido", titulo: "Envolvidas + categorias (padrão)", desc: "Vê onde está alocado, onde criou/aprova e os projetos das categorias atribuídas a ele." },
  { id: "todas", titulo: "Todas as tarefas", desc: "Funcionário vê todas as tarefas, como gestor." },
];

function OperacaoConfigEditor() {
  const qc = useQueryClient();
  const { data: cfg } = useQuery({ queryKey: ["operacao-config"], queryFn: getOperacaoConfig });
  const [draft, setDraft] = useState<OperacaoConfig | null>(null);
  const salvarM = useMutation({
    mutationFn: (v: OperacaoConfig) => setOperacaoConfig(v),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["operacao-config"] }); qc.invalidateQueries({ queryKey: ["tarefas"] }); setDraft(null); },
  });
  const atual = draft || cfg;
  if (!atual) return null;
  const set = (patch: Partial<OperacaoConfig>) => setDraft({ ...atual, ...patch });

  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4 space-y-4">
      <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Operação — visibilidade e pausa</h3>

      {/* Visibilidade do funcionário */}
      <div>
        <p className="text-xs font-medium text-slate-600 dark:text-slate-300 mb-1.5">O que o funcionário enxerga no quadro</p>
        <div className="space-y-1.5">
          {VIS_OPCOES.map((o) => (
            <label key={o.id} className={`flex items-start gap-2 p-2 rounded-lg border cursor-pointer ${atual.visibilidadeFuncionario === o.id ? "border-blue-300 bg-blue-50/50 dark:bg-blue-900/20" : "border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/40"}`}>
              <input type="radio" name="vis" checked={atual.visibilidadeFuncionario === o.id} onChange={() => set({ visibilidadeFuncionario: o.id })} className="mt-0.5" />
              <div>
                <p className="text-sm text-slate-800 dark:text-slate-100">{o.titulo}</p>
                <p className="text-xs text-slate-500">{o.desc}</p>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Pausa automática */}
      <div className="border-t border-slate-100 dark:border-slate-800 pt-3 space-y-2">
        <p className="text-xs font-medium text-slate-600 dark:text-slate-300">Pausa automática do play</p>
        <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
          <input type="checkbox" checked={atual.autoPausarFimExpediente} onChange={(e) => set({ autoPausarFimExpediente: e.target.checked })} />
          Pausar automaticamente ao fim do expediente (fora do horário comercial)
        </label>
        <div className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={atual.autoPausarTtlHoras != null} onChange={(e) => set({ autoPausarTtlHoras: e.target.checked ? 10 : null })} />
            Pausar play esquecido aberto há mais de
          </label>
          <input
            type="number" min={1} step={1}
            value={atual.autoPausarTtlHoras ?? ""}
            disabled={atual.autoPausarTtlHoras == null}
            onChange={(e) => set({ autoPausarTtlHoras: e.target.value === "" ? null : Math.max(1, Number(e.target.value) || 1) })}
            className="w-16 px-2 py-1 rounded border border-slate-300 dark:border-slate-600 dark:bg-slate-800 text-sm text-center outline-none disabled:opacity-40"
          />
          <span>horas</span>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button onClick={() => salvarM.mutate(atual)} disabled={salvarM.isPending || !draft} className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 py-1.5 rounded-lg disabled:opacity-50">
          <Save size={14} /> Salvar
        </button>
        {draft && <button onClick={() => setDraft(null)} className="text-sm text-slate-500 px-2 py-1.5 hover:text-slate-700">Cancelar</button>}
      </div>
    </div>
  );
}

function HorarioComercialEditor() {
  const qc = useQueryClient();
  const { data: cfg } = useQuery({ queryKey: ["horario-comercial"], queryFn: getHorarioComercial });
  const [draft, setDraft] = useState<HorarioComercial | null>(null);
  const atual = draft || cfg;

  const salvar = useMutation({
    mutationFn: (v: HorarioComercial) => setHorarioComercial(v),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["horario-comercial"] });
      qc.invalidateQueries({ queryKey: ["historico-sessoes"] });
      setDraft(null);
    },
  });

  if (!atual) return null;
  const DIAS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

  function toggleDia(d: number) {
    if (!atual) return;
    const set = new Set(atual.diasUteis);
    if (set.has(d)) set.delete(d); else set.add(d);
    setDraft({ janelas: atual.janelas, diasUteis: Array.from(set).sort() });
  }
  function setJanela(i: number, campo: "inicio" | "fim", v: string) {
    if (!atual) return;
    const js = atual.janelas.map((j, idx) => (idx === i ? { ...j, [campo]: v } : j));
    setDraft({ janelas: js, diasUteis: atual.diasUteis });
  }
  function addJanela() {
    if (!atual) return;
    setDraft({ janelas: [...atual.janelas, { inicio: "09:00", fim: "12:00" }], diasUteis: atual.diasUteis });
  }
  function removeJanela(i: number) {
    if (!atual) return;
    setDraft({ janelas: atual.janelas.filter((_, idx) => idx !== i), diasUteis: atual.diasUteis });
  }

  return (
    <div className="bg-white dark:bg-slate-900 border border-blue-200 dark:border-blue-800 rounded-xl p-4 space-y-3">
      <h3 className="font-semibold text-slate-800 dark:text-slate-100 text-sm">Horário comercial</h3>
      <div>
        <p className="text-xs text-slate-500 mb-1">Dias da semana</p>
        <div className="flex gap-1.5 flex-wrap">
          {DIAS.map((d, i) => (
            <button
              key={i}
              type="button"
              onClick={() => toggleDia(i)}
              className={`text-xs px-2.5 py-1 rounded border ${
                atual.diasUteis.includes(i)
                  ? "bg-blue-50 border-blue-300 text-blue-700"
                  : "bg-white border-slate-200 text-slate-500 hover:bg-slate-50"
              }`}
            >
              {d}
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="text-xs text-slate-500 mb-1">Janelas do dia</p>
        <div className="space-y-1.5">
          {atual.janelas.map((j, i) => (
            <div key={i} className="flex items-center gap-2">
              <input type="time" value={j.inicio} onChange={(e) => setJanela(i, "inicio", e.target.value)} className="px-2 py-1 rounded border border-slate-300 text-sm" />
              <span className="text-slate-400">até</span>
              <input type="time" value={j.fim} onChange={(e) => setJanela(i, "fim", e.target.value)} className="px-2 py-1 rounded border border-slate-300 text-sm" />
              {atual.janelas.length > 1 && (
                <button type="button" onClick={() => removeJanela(i)} className="text-xs text-red-600 hover:underline">remover</button>
              )}
            </div>
          ))}
          <button type="button" onClick={addJanela} className="text-xs text-blue-600 hover:underline">+ adicionar janela</button>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={!draft || salvar.isPending}
          onClick={() => draft && salvar.mutate(draft)}
          className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs px-3 py-1.5 rounded-lg"
        >
          <Save size={12} /> Salvar
        </button>
        {draft && <button type="button" onClick={() => setDraft(null)} className="text-xs text-slate-500 hover:underline">Cancelar</button>}
      </div>
    </div>
  );
}
